/* eslint-disable @next/next/no-img-element */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse } from '@material-ui/core';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import H5 from '@base/H5/H5';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import KeyboardArrowUp from '@icon/KeyboardArrowUp';
import { mobile, Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import textHelper from '@utils/textHelper';
import { useIsMobile } from '@stores/AppCommonData';
import { getImage } from '@utils/imageHelper';
import ScaledImage from './ScaledImage';

const Wrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  margin-bottom: 160px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 56px;
  h2 {
    margin-top: 20px;
    margin-bottom: 12px;
    @media only screen and (max-width: ${mobile.maxWidth}) {
      font-size: 24px;
    }
  }
  p {
    font-size: 16px;
    color: var(--color-text-secondary);
  }
`;

const QuestionsWrapper = styled.div`
  display: grid;
  grid-gap: ${Spacings.SPACING_4B};
  grid-template-columns: 1fr 1fr;
  height: ${({ $count }) => `${$count * 112 + ($count - 1) * 16}px`};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    width: 95%;
    margin: auto;
    grid-template-columns: 1fr;
  }
`;

const FAQWrapper = styled.div`
  background: var(--color-i-white);
  min-height: 112px;
  height: max-content;
  box-shadow: ${boxShadowGenerator({ color: 'var(--color-divider)' })};
  border-radius: 8px;
  padding: 32px;
  cursor: pointer;
  user-select: none;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    width: 95%;
    margin: auto;
  }
`;

const Question = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Answer = styled(P1)`
  margin-top: 12px;
  color: var(--color-text-secondary);
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transition: 100ms opacity ease-in;
  position: relative;
  z-index: 2;
`;

const StyledLogo = styled(ScaledImage)`
  margin: auto;
`;

const CombatFAQs = ({ goalUID }) => {
  const isMobile = useIsMobile();
  const { faqs } = useSelector(
    ({ combats }) => combats.staticData[goalUID] || []
  );
  const [faqOpen, setFaqOpen] = useState(null);

  const openFAQ = (i) => {
    setFaqOpen(i);
  };

  const closeFAQ = () => {
    setFaqOpen(null);
  };

  return (
    <Wrapper>
      <Header>
        <StyledLogo $size={49}>
          <img src={getImage('combat/icon - 8.svg')} alt="faq-logo" />
        </StyledLogo>
        <H2>Frequently asked questions</H2>
      </Header>
      <QuestionsWrapper
        $count={(isMobile ? faqs.length : Math.ceil(faqs.length / 2)) || 0}
      >
        {faqs.map((faq, i) => {
          const isFaqOpen = faqOpen === i;
          return (
            <FAQWrapper
              key={`faq-${faq.id}`}
              onClick={isFaqOpen ? closeFAQ : () => openFAQ(i)}
            >
              <Question>
                <H5>{faq.question}</H5>
                {isFaqOpen ? (
                  <KeyboardArrowUp size="24px" color="black" />
                ) : (
                  <KeyboardArrowDown size="24px" color="black" />
                )}
              </Question>
              <Collapse in={isFaqOpen} timeout="auto" unmountOnExit>
                <Answer
                  isOpen={isFaqOpen}
                  dangerouslySetInnerHTML={{
                    __html: textHelper.replaceUrlsWithAnchorTags(faq.answer)
                  }}
                />
              </Collapse>
            </FAQWrapper>
          );
        })}
      </QuestionsWrapper>
    </Wrapper>
  );
};

export default CombatFAQs;

CombatFAQs.propTypes = {
  goalUID: PropTypes.string.isRequired
};
